import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { isoDateTime, z } from "../zod.js";
import { AuditChainVerification, AuditEntryListResponse } from "./schemas.js";

// The audit-trail READ surface (spec 16 §5). Two GETs, no mutations — this
// package registers nothing that could write, amend or re-hash a chain row.
//
// Tagged `audit` rather than `identity`: the chain is written by every module
// and owned by none (spec 16 §2.16 — "owned by no single module, written by
// all"), so grouping it under identity's tag would misdescribe it in every
// generated client. It sits beside `files` as a platform-owned route group.

const sessionSecurity = [{ sessionCookie: [] }];

function route(config: {
  method: "get";
  path: string;
  operationId: string;
  summary: string;
  permission: string;
  description: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
}): void {
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["audit"],
    summary: config.summary,
    description: `${config.description} Requires \`${config.permission}\` AND group scope.`,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

route({
  method: "get",
  path: "/v1/audit/entries",
  operationId: "listAuditEntries",
  summary: "The tenant's hash-chained audit trail (§2.16)",
  permission: "identity.view_auth_events",
  description:
    "Newest-first page of the tenant's audit chain, keyset-paginated on `seq` " +
    "(dense and unique per chain, so a page boundary can neither skip nor repeat). " +
    "Filter by entity (`entity_type` + `entity_id` — everything that happened to THIS " +
    "hold / product / work order), by `actor`, by `action` or `action_prefix`, and by " +
    "`from`/`to`. The `chain` object reports what was verified ABOUT THIS PAGE and " +
    "nothing more; use `/v1/audit/chain/verify` for a statement about the chain. " +
    "A row's `payload` is served only to holders of the ORIGINATING module's read " +
    "permission and is otherwise absent, with `payload_redacted: true`. No row carries " +
    "its hashes (D-337 ruling 3): the chain is unkeyed, so a served hash would let a " +
    "reader test guesses of every payload before it that is withheld from them.",
  request: {
    query: z.object({
      entity_type: z.string().min(1).max(120).optional(),
      /** Free text, not a uuid: two writers use a natural key as `entity_id`. */
      entity_id: z.string().min(1).max(200).optional(),
      /** Verbatim `actor` column, e.g. `user:<uuid>` or `system`. */
      actor: z.string().min(1).max(200).optional(),
      /** Exact action match, e.g. `quality.hold_released`. */
      action: z.string().min(1).max(120).optional(),
      /** Prefix match, e.g. `quality.` or `production.work_order_`. */
      action_prefix: z.string().min(1).max(120).optional(),
      from: isoDateTime().optional(),
      to: isoDateTime().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: {
    200: {
      description: "A page of the chain, with this page's integrity report",
      content: { "application/json": { schema: AuditEntryListResponse } },
    },
    403: problemResponse(
      "PERMISSION_DENIED — missing identity.view_auth_events / " +
        "AUDIT_GROUP_SCOPE_REQUIRED — the chain is tenant-wide and cannot be facility-fenced",
    ),
  },
});

route({
  method: "get",
  path: "/v1/audit/chain/verify",
  operationId: "verifyAuditChain",
  summary: "Verify a contiguous range of the tenant's audit chain",
  permission: "identity.view_auth_events",
  description:
    "Walks an UNFILTERED, contiguous `seq` range with the same hash primitive the writer " +
    "and the test-kit verifier share: seq contiguity, prev→curr linkage, and recomputation " +
    "of every `curr_hash` from its row content. Defaults to the whole chain. " +
    "`anchored_to_genesis` is true only when a row at seq 1 was walked AND its link is the " +
    "genesis hash — a range above it is internally consistent and nothing about it proves " +
    "descent from genesis. Capped per call; when truncated, `complete` is false and " +
    "`next_from_seq` resumes, overlapping the last verified row by one so the link across " +
    "the chunk boundary is checked too. `next_from_seq` is null when there is nothing " +
    "further to walk, which is the condition a client loop must terminate on.",
  request: {
    query: z.object({
      from_seq: z.coerce.number().int().min(1).optional(),
      to_seq: z.coerce.number().int().min(1).optional(),
    }),
  },
  responses: {
    200: {
      description: "The verification verdict for the walked range",
      content: { "application/json": { schema: AuditChainVerification } },
    },
    403: problemResponse(
      "PERMISSION_DENIED — missing identity.view_auth_events / AUDIT_GROUP_SCOPE_REQUIRED",
    ),
    422: problemResponse("AUDIT_RANGE_INVALID — to_seq is below from_seq"),
  },
});
